import { decodeHtmlEntities } from 'utils/decodeHtmlEntities';
import { isSafeHref } from 'utils/urlScheme';

const OPENING_LINK_TAG = /^<a\s[^>]*>$/i;
const CLOSING_LINK_TAG = /^<\/a\s*>$/i;

export const escapeAttr = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const isClosingLinkTag = (raw: string): boolean => CLOSING_LINK_TAG.test(raw);

function attribute(tag: string, name: string): string | undefined {
  const match = new RegExp(`\\s${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i').exec(tag);
  return match ? (match[1] ?? match[2] ?? match[3]) : undefined;
}

/**
 * A raw `<a>` opening tag rebuilt from its address alone: `null` when `raw` is not an opening
 * link tag at all, so the caller can fall through to other handling; `''` when it is one but has
 * no safe address. Only `href` survives, plus `target="_blank"` with `rel="noopener noreferrer"`
 * when the author asked for a new tab; every other attribute, event handlers included, is dropped.
 *
 * The href is read as the attribute's literal text, which is entity-encoded exactly like any
 * other HTML attribute (`&amp;` for a literal `&`), so it is decoded once — the same decoder used
 * for plain text, since this is the one case it is safe on a markdown sink: the decoded value is
 * immediately checked and re-escaped by `escapeAttr`, never written out as-is — before the safety
 * check and the rebuild. Checking the still-encoded form would miss an encoded scheme
 * (`javascript&colon;`); rebuilding from the still-encoded form would double-encode a literal `&`
 * already in the address.
 */
export function buildSafeOpeningLinkTag(raw: string): string | null {
  if (!OPENING_LINK_TAG.test(raw)) return null;
  const rawHref = attribute(raw, 'href')?.trim();
  const href = rawHref ? decodeHtmlEntities(rawHref) : undefined;
  if (!href || !isSafeHref(href)) return '';
  const newTab = attribute(raw, 'target')?.trim().toLowerCase() === '_blank';
  return `<a href="${escapeAttr(href)}"${newTab ? ' target="_blank" rel="noopener noreferrer"' : ''}>`;
}
